import type { AnyEventBase } from '@snapchat/blizzard-schema';
import { stringifyBlizzardJsonArray } from '../logEvent/json';
import { tryToGetUploadConfig } from './getUploadConfig';
import { contentTypeJson, statusesPossibleAuthFailure } from './constants';
import { getLogger } from '../logger';
import { fetchRetry } from './fetchRetry';
import { logDebugAction } from '../debug/debug';
/**
 * Transmit the provided events as JSON.
 *
 * @param events
 * @internal
 */
export async function transmitJsonEvents(events: AnyEventBase[]): Promise<void> {
  // Serialize events
  const jsonBody = stringifyBlizzardJsonArray(events);

  await upload(jsonBody, contentTypeJson, events.length);
  logDebugAction({ type: 'EventsUploaded', events });
}

/** TODO(kho): TSDoc */
async function upload(body: BodyInit, contentType: string, count: number): Promise<void> {
  log.debug(`Uploading ${count} event(s)... [${contentType}]`);

  let uploadConfig = await tryToGetUploadConfig();
  if (!uploadConfig) {
    log.error('Failed to get upload config after several attempts. Discarding upload attempt.');
    return;
  }
  const success = await fetchRetry(`${uploadConfig.baseUrl}/events`, () => ({
    method: 'POST',
    headers: {
      'Content-Type': contentType,
      Authorization: uploadConfig!.authorizationHeader,
      'X-Blizzard-Upload-Timestamp': `${Date.now()}`, // Milliseconds.
    },
    body,
    credentials: uploadConfig!.credentials,
    keepalive: true,
    onStatus: async status => {
      if (statusesPossibleAuthFailure.includes(status)) {
        // Refresh authentication if possible before retrying. If that errored out,
        // try with the original upload config again.
        log.info('Auth failure, attempting to refresh authentication...');
        uploadConfig = (await tryToGetUploadConfig(uploadConfig)) ?? uploadConfig;
      }
    },
  }));

  if (success) {
    // Note: manual QA currently relies on checking for this line. Don't modify.
    log.debug(`Uploaded ${count} event(s)`);
  } else {
    log.error('Failed to upload events after retries, discarding upload attempt.');
  }
}

const log = getLogger('transmit');
